import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ModulesContext = createContext();

export const useModules = () => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within ModulesProvider');
  }
  return context;
};

export const ModulesProvider = ({ children }) => {
  // 🔒 PAYWALL: Start with EMPTY modules (locked by default until API confirms access)
  const [enabledModules, setEnabledModules] = useState([]);
  const [isV2Catalog, setIsV2Catalog] = useState(false);
  const [moduleSource, setModuleSource] = useState('unknown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Retry-Zähler: verhindert Endlosschleifen wenn userRole dauerhaft unbekannt
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 8;          // 8 × 400ms = max 3.2s Wartezeit

  useEffect(() => {
    // Wait a bit for the user to be logged in
    const timer = setTimeout(() => {
      fetchEnabledModules();
    }, 500);
    
    // Listen for login events to reload modules
    const handleLogin = () => {
      console.log('[ModulesContext] User logged in, reloading modules...');
      retryCountRef.current = 0;   // Reset retry count on explicit login
      setTimeout(() => {
        fetchEnabledModules();
      }, 300);
    };
    
    // Listen for module updates from ModuleManagement page
    const handleModulesUpdated = () => {
      console.log('[ModulesContext] Modules updated, reloading...');
      fetchEnabledModules();
    };
    
    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('modulesUpdated', handleModulesUpdated);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('modulesUpdated', handleModulesUpdated);
    };
  }, []);

  const fetchEnabledModules = async () => {
    // ── FIX: Früh-Check ob User+Role schon bekannt ist ─────────────────────────
    // RACE CONDITION FIX: Wenn der 500ms-Timer feuert bevor AuthContext den User
    // in localStorage gespeichert hat, ist userRole undefined → vorher ging
    // der Code in den else-Branch und setzte enabledModules=[], loading=false,
    // was die komplette Sidebar kollabieren ließ.
    // Jetzt: bei unbekannter Role NICHT loading=false setzen, sondern retry.
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[ModulesContext] No token, using default modules');
      setLoading(false);
      return;
    }

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.role;
    const knownRoles = ['admin', 'superadmin', 'employee', 'stationsleitung'];

    if (!knownRoles.includes(userRole)) {
      // Auth noch nicht fertig initialisiert — retry statt Module sperren!
      retryCountRef.current += 1;
      if (retryCountRef.current < MAX_RETRIES) {
        console.log(`[ModulesContext] ⏳ User-Role noch unbekannt (${userRole}), Retry ${retryCountRef.current}/${MAX_RETRIES}...`);
        setTimeout(fetchEnabledModules, 400);
        return; // loading=true bleibt erhalten → Sidebar bleibt vollständig
      }
      // Max Retries erreicht (z.B. nicht eingeloggt) → loading beenden
      console.warn('[ModulesContext] ⚠️ Max Retries erreicht, unknown role. Loading beendet.');
      setEnabledModules([]);
      setLoading(false);
      return;
    }

    // ── Role bekannt: Retry-Zähler zurücksetzen + API-Fetch ───────────────────
    retryCountRef.current = 0;
    console.log('[ModulesContext] Fetching enabled modules for role:', userRole);

    try {
      let modules = [];
      
      // DIFFERENT LOGIC FOR ADMIN VS EMPLOYEE/STATIONSLEITUNG
      if (userRole === 'admin' || userRole === 'superadmin') {
        // ADMIN: Get UNION of all enabled modules across ALL their locations
        try {
          const response = await axios.get(`${API}/api/admin/enabled-modules`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          modules = response.data.enabled_modules || [];
          const source = response.data.source || 'unknown';
          const locationCount = response.data.location_count || 0;
          
          // source=='billing_v2_catalog' → V2-Kunde, keine Industry-Filterung nötig
          setIsV2Catalog(source === 'billing_v2_catalog');
          setModuleSource(source);
          
          console.log(`[ModulesContext] ✅ Admin modules loaded (${source}, ${locationCount} locations):`, modules);
        } catch (error) {
          console.error('[ModulesContext] ❌ Failed to fetch admin enabled modules:', error);
          
          // 🔒 PAYWALL: If API call fails, modules remain LOCKED (empty array)
          if (error.response?.status === 402 || error.response?.data?.locked) {
            console.log('[ModulesContext] 🔒 Payment required or locked - all modules locked');
            modules = [];
          } else {
            console.error('[ModulesContext] ❌ API error - locking all modules for security');
            modules = [];
          }
        }
      } else if (userRole === 'employee' || userRole === 'stationsleitung') {
        // EMPLOYEE & STATIONSLEITUNG: Use the SAME endpoint as admin (backend handles the logic)
        try {
          const response = await axios.get(`${API}/api/admin/enabled-modules`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          modules = response.data.enabled_modules || [];
          console.log('[ModulesContext] ✅ Employee modules loaded for their location:', modules);
        } catch (error) {
          console.error('[ModulesContext] ❌ Failed to fetch employee modules:', error);
          modules = [];
        }
      }
      
      setEnabledModules(modules);
      setError(null);
    } catch (error) {
      console.error('[ModulesContext] ❌ Failed to fetch modules:', error);
      setError('Fehler beim Laden der Module');
      setEnabledModules([]);
    } finally {
      setLoading(false);
    }
  };

  const isModuleEnabled = (moduleId) => {
    if (!moduleId) return true; // No module restriction
    
    // Map Navigation IDs to Database Module IDs
    const moduleIdMap = {
      // Navigation ID → DB Module ID
      'mhd': 'mhd_control',
      'temperature': 'temperature_control',
      'instructions': 'unterweisungen',
      'orders': 'bestellungen',
      // These are already correct:
      'backshop': 'backshop',
      'lotto': 'lotto',
      'tasks': 'tasks',
      'general_writeoffs': 'general_writeoffs',
      'employee_management': 'employee_management',
      'unterweisungen': 'unterweisungen'
    };
    
    // Get the DB module ID
    const dbModuleId = moduleIdMap[moduleId] || moduleId;
    
    // Check if module is enabled (check both formats for safety)
    return enabledModules.includes(moduleId) || enabledModules.includes(dbModuleId);
  };

  const refreshModules = () => {
    setLoading(true);
    setError(null);
    fetchEnabledModules();
  };

  return (
    <ModulesContext.Provider 
      value={{ 
        enabledModules, 
        isModuleEnabled, 
        loading, 
        error,
        refreshModules,
        isV2Catalog,  // true wenn Module aus Billing Catalog kommen (V2-Kunde)
        moduleSource, // 'billing_v2_catalog' | 'location_configs' | 'superadmin' | ...
      }}
    >
      {children}
    </ModulesContext.Provider>
  );
};
